import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@shared/components/shadcn';
import React from 'react';

type TableColumn = {
  header: string;
  width?: string;
  align?: 'left' | 'center' | 'right';
  mobileAlign?: 'left' | 'center' | 'right';
};

type TableRowData = {
  cells: (string | React.ReactNode)[];
};

type DataTableProps = {
  columns: TableColumn[];
  rows: TableRowData[];
  caption?: string;
} & React.ComponentProps<'div'>;

function DataTable({ columns, rows, caption, className = '', ...props }: DataTableProps) {
  const tableId = React.useId();
  const { 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy, ...divProps } = props;

  function getTextAlignClass(align?: 'left' | 'center' | 'right'): string {
    if (align === 'right') return 'text-right';
    if (align === 'center') return 'text-center';
    return 'text-left';
  }

  function getMobileAlign(cellIndex: number): 'left' | 'center' | 'right' {
    const column = columns[cellIndex];
    return (column?.mobileAlign || column?.align || 'left') as 'left' | 'center' | 'right';
  }

  return (
    <div className={`rounded-lg border border-border bg-container-base ${className}`} {...divProps}>
      {/* Desktop Table View */}
      <div className="hidden md:block">
        <Table className="data-table-table" role="table" aria-label={ariaLabel} aria-describedby={ariaDescribedBy}>
          {caption && <caption className="sr-only">{caption}</caption>}
          <TableHeader className="data-table-header border-b border-border [&_tr]:border-0">
            <TableRow>
              {columns.map((column, index) => (
                <TableHead
                  key={index}
                  id={`${tableId}-header-${index}`}
                  scope="col"
                  className={`${column.width || 'w-auto'} data-table-header-cell ${getTextAlignClass(column.align)}`}
                >
                  {column.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody className="data-table-body">
            {rows.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {row.cells.map((cell, cellIndex) => (
                  <TableCell key={cellIndex} className={`data-table-cell ${getTextAlignClass(columns[cellIndex]?.align)}`} headers={`${tableId}-header-${cellIndex}`}>
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden" role="region" aria-label={ariaLabel ? `${ariaLabel} - Mobile View` : 'Table data - Mobile View'} aria-describedby={ariaDescribedBy}>
        {rows.map((row, rowIndex) => (
          <div
            key={rowIndex}
            className={`border-b border-border last:border-b-0 ${rowIndex % 2 === 0 ? 'data-table-mobile-row-even' : 'data-table-mobile-row-odd'}`}
            role="row"
            aria-rowindex={rowIndex + 2} // +2 because header is row 1
          >
            <div className="p-4">
              <div className="grid grid-cols-[1fr_2fr] gap-x-4 gap-y-2">
                {row.cells.map((cell, cellIndex) => (
                  <React.Fragment key={cellIndex}>
                    <div
                      className={`data-table-mobile-header-cell ${getTextAlignClass(getMobileAlign(cellIndex))}`}
                      role="columnheader"
                      aria-label={`${columns[cellIndex]?.header}:`}
                    >
                      {columns[cellIndex]?.header}
                    </div>
                    <div
                      className={`data-table-mobile-data-cell ${getTextAlignClass(getMobileAlign(cellIndex))}`}
                      role="cell"
                      aria-label={`${columns[cellIndex]?.header}: ${typeof cell === 'string' ? cell : 'Content'}`}
                    >
                      {cell}
                    </div>
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export { DataTable };
export type { DataTableProps, TableColumn, TableRowData };
